import { Center, GridItem, Image } from '@chakra-ui/react';
import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Control, Controller, FieldErrors, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { FiXCircle } from 'react-icons/fi';
import { FileUploadDropzone, FileUploadList, FileUploadRoot, Field } from '@/components';
import { fileAccept, IS_CHECK_FILE_SIZE } from '@/constants';
import { useConfirm } from '@/hooks';
import { trans } from '@/i18n';
import { IBaseUploadRef } from '@/types';
import { msgInvalidFileSize, showError } from '@/utils';
import { getStaticFile } from '@/utils/path';

interface IProps {
  span?: number;
  name: string;
  label: string;
  disabled?: boolean;
  hideLabel?: boolean;
  placeholder?: string;
  showFileList?: boolean;
  control?: Control<any>;
  errors?: FieldErrors<any>;
  maxFiles?: number;
  accept?: string[];
  imageUrl?: string;
  onRemove?: () => void;
  setValue: UseFormSetValue<any>;
  trigger: UseFormTrigger<any>;
  hideDescription?: boolean;
  size?: number;
  className?: string;
  description?: string;
  fileSize?: number;
  fileSizeName?: string;
  confirmChangeFile?: boolean;
  onChange?: () => void;
}

function Component(props: IProps, ref: ForwardedRef<IBaseUploadRef>) {
  const [imageUrl, setImageUrl] = useState<string | undefined>(getStaticFile(props.imageUrl));
  const [tmpFile, setTmpFile] = useState<File>();

  const { toggleConfirm } = useConfirm();

  useEffect(() => {
    setImageUrl(getStaticFile(props.imageUrl));
  }, [props.imageUrl]);

  const fileRef = useRef<HTMLInputElement>(null);

  const handleChangeFile = (file: File) => {
    if (!file) return;
    if (typeof file === 'string') {
      setImageUrl(file);
      props.setValue(props.name, file);
      props.trigger(props.name);
      return;
    }

    if (IS_CHECK_FILE_SIZE && props.fileSize && file.size > props.fileSize) {
      showError(msgInvalidFileSize(props.fileSizeName || '10MB'));
      return;
    }

    const url = URL.createObjectURL(file);
    setImageUrl(url);
    props.setValue(props.name, file);
    props.trigger(props.name);
    setTmpFile(file);
    props.onChange?.();
  };

  const handleBeforeChange = (details: any) => {
    const file = details?.acceptedFiles?.[0];
    if (!file) return;
    if (imageUrl)
      toggleConfirm({
        title: 'データが上書きされます',
        message: '上書きすると復元できませんがよろしいでしょうか？',
        okText: 'はい',
        cancelText: 'いいえ',
        onOk: async () => {
          handleChangeFile(file);
        },
        onClose: () => {
          // handleChangeFile(tmpFile! || imageUrl);
        },
      });
    else {
      handleChangeFile(file);
    }
  };

  const handleRemoveFile = () => {
    if (!!tmpFile || !!imageUrl) {
      toggleConfirm({
        title: 'データが削除されます',
        message: '削除すると復元できませんがよろしいでしょうか？',
        okText: 'はい',
        cancelText: 'いいえ',
        onOk: async () => {
          if (fileRef.current) {
            fileRef.current.value = '';
          }
          props.onRemove?.();
          setImageUrl(undefined);
          props.setValue(props.name, undefined);
          props.trigger(props.name);
        },
        onClose: () => {},
      });
      return;
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      openFile: () => {
        fileRef.current?.click();
      },
    }),
    []
  );

  return (
    <GridItem className={props.className} colSpan={props.span || 6}>
      <Controller
        control={props.control}
        rules={{
          required: true,
        }}
        // render={({ field: { onChange, onBlur, value } }) => (
        render={() => (
          <Field
            label={!props.hideLabel ? props.label : undefined}
            invalid={!!props.errors?.[props.name]}
            errorText={props.errors?.[props.name]?.message}
          >
            <FileUploadRoot
              onFileChange={handleBeforeChange}
              accept={props.accept || fileAccept.texture}
              maxW="full"
              alignItems="stretch"
              maxFiles={props.maxFiles}
              pos="relative"
              ref={fileRef}
            >
              <FileUploadDropzone
                label={trans(props.description || 'upload.title')}
                description={!props.hideDescription ? trans('upload.subtitle') : undefined}
              />
              {props.showFileList && <FileUploadList />}
              {imageUrl && (
                <Center width="full" zIndex={100} pos="absolute">
                  <Image
                    borderRadius="0.375rem"
                    height="256px"
                    width="full"
                    top="0"
                    src={imageUrl}
                  />
                  <FiXCircle
                    z={1}
                    onClick={handleRemoveFile}
                    color="red"
                    className="ic-close-upload"
                  />
                </Center>
              )}
            </FileUploadRoot>
          </Field>
        )}
        name={props.name}
      />
    </GridItem>
  );
}

export const BaseUpload = forwardRef(Component);
